/**
 * SWUpdatePrompt — surfaces a tasteful toast whenever a new service worker
 * is installed and waiting to take over.
 *
 * Background: service workers don't replace the current build until every
 * tab using the old SW is closed. That's why users sometimes refresh and
 * still see the previous UI ("back-and-forth" reports). The SW we ship is
 * already wired to listen for `SKIP_WAITING` messages, so clicking the
 * refresh button promotes the waiting worker and forces a one-shot reload
 * with the fresh bundle.
 *
 * The toast is intentionally minimal (sonner one-liner with an action).
 * No modal, no overlay, no friction.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

function showUpdateToast(waitingWorker) {
  toast.message('A new version is ready', {
    description: 'Refresh to load the latest dashboard and fixes.',
    duration: Infinity,
    action: {
      label: 'Refresh',
      onClick: () => {
        try {
          // Tell the waiting SW to take over immediately. Once it does, the
          // browser fires `controllerchange` (handled below) and we reload.
          waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        } catch {
          // Worst case — fall back to a manual reload.
          window.location.reload();
        }
      },
    },
    id: 'sw-update-toast',
  });
}

const SWUpdatePrompt = () => {
  const reloadedRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    let cancelled = false;

    // Auto-reload the moment the new SW takes control. Guards against an
    // infinite reload loop with the ref.
    const onControllerChange = () => {
      if (reloadedRef.current) return;
      reloadedRef.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    // Watch for `updatefound` on the existing registration. If a new SW
    // is installed and there's already an active SW, prompt the user.
    const wireRegistration = (registration) => {
      if (!registration || cancelled) return;
      // Case 1: a worker is already waiting when we register
      if (registration.waiting && navigator.serviceWorker.controller) {
        showUpdateToast(registration.waiting);
      }
      // Case 2: a new worker shows up while the user is browsing
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            showUpdateToast(newWorker);
          }
        });
      });
    };

    navigator.serviceWorker.getRegistration('/service-worker.js')
      .then(wireRegistration)
      .catch(() => {/* SW not installed yet — nothing to do */});

    // Poll for updates every 30 minutes — long-lived tabs (e.g. someone
    // who keeps iStudyPro open all afternoon) still pick up new builds.
    const interval = setInterval(() => {
      navigator.serviceWorker.getRegistration('/service-worker.js')
        .then((reg) => reg && reg.update())
        .catch(() => {});
    }, 30 * 60 * 1000);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      clearInterval(interval);
    };
  }, []);

  return null;
};

export default SWUpdatePrompt;
